import { useParams, Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import TrustBar from '../../../components/feature/TrustBar';
import ProcessSection from '../../../components/feature/ProcessSection';
import WhyChooseUs from '../../../components/feature/WhyChooseUs';
import TestimonialsSection from '../../../components/feature/TestimonialsSection';
import FAQSection from '../../../components/feature/FAQSection';
import FinalCTA from '../../../components/feature/FinalCTA';
import AIAnswerBlock from '../../../components/feature/AIAnswerBlock';
import ServiceAreasSection from '../../../components/feature/ServiceAreasSection';
import ServicesOverview from '../../home/components/ServicesOverview';
import { COMPANY, SERVICE_AREAS } from '../../../mocks/siteData';

export default function LocationPage() {
  const { city } = useParams<{ city: string }>();
  const areaData = SERVICE_AREAS.find((a) => a.slug === city);
  const cityDisplay = areaData?.name ?? (city ?? 'Your Area').replace(/-/g, ' ').replace(/\bfl\b/i, 'FL');

  const faqs = [
    {
      q: `Who does remodeling in ${cityDisplay}?`,
      a: `Bay City Remodeling & Bathrooms LLC serves homeowners in ${cityDisplay} with full-service kitchen, bathroom, and full-home remodeling solutions — backed by 18+ years of local experience and a 5.0-star rating across Brandon, FL and surrounding communities.`,
    },
    {
      q: `What does remodeling cost in ${cityDisplay}?`,
      a: `Costs depend on project size, materials, and scope. Bay City Remodeling provides free in-home consultations with accurate, itemized estimates so ${cityDisplay} homeowners know exactly what to expect before work begins.`,
    },
    {
      q: `How long do remodeling projects take in ${cityDisplay}?`,
      a: `Timelines vary based on project complexity. Most remodels range from several weeks to a few months. We provide a detailed project schedule before starting every project in ${cityDisplay}.`,
    },
    {
      q: `Do you serve all of ${cityDisplay}?`,
      a: `Yes. Bay City Remodeling serves homeowners throughout ${cityDisplay} and surrounding communities in the greater Brandon, FL area. If you\'re unsure, call us and we\'ll confirm immediately.`,
    },
  ];

  return (
    <div className="font-sans">
      <Navbar />

      {/* 1. Hero */}
      <section
        className="relative min-h-[92vh] w-full flex items-end overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/29fcbf916fa5cf1d70c1d89351258782.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/55 to-stone-900/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 via-stone-950/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-stone-950 to-transparent" />

        <div className="relative z-10 w-full px-6 lg:px-16 xl:px-24 pb-20 pt-40">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-7">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-accent text-xs" />
                </div>
              ))}
              <div className="w-px h-4 bg-white/20" />
              <span className="text-white/55 text-xs">{COMPANY.rating} · {COMPANY.reviewCount} Reviews</span>
            </div>
            <div className="lg:pl-7 relative">
              <div className="absolute left-0 top-0 w-[2px] h-full bg-gradient-to-b from-accent via-accent/50 to-transparent hidden lg:block" />
              <h1 className="font-serif text-[50px] md:text-[66px] lg:text-[78px] text-white leading-[1.0] tracking-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
                Home Remodeling in {cityDisplay}
              </h1>
            </div>
            <p className="text-white/60 text-base md:text-lg mb-10 leading-relaxed max-w-2xl">
              Bay City Remodeling delivers premium kitchen, bathroom, and full-home remodeling services to homeowners in {cityDisplay} — quality craftsmanship, transparent pricing, and a process built around your vision.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/about"
                className="group inline-flex items-center gap-3 bg-accent hover:bg-accent-dark text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                Request a Consultation
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
                </div>
              </Link>
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 bg-white/5 text-white font-medium px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent" />
                </div>
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview */}
      <ServicesOverview />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        question={`Who Provides Remodeling in ${cityDisplay}?`}
        answer={`Bay City Remodeling & Bathrooms LLC offers kitchen, bathroom, and full-home remodeling services in ${cityDisplay}, delivering high-quality renovations tailored to local homes and homeowner needs. The company brings 18+ years of remodeling experience and a 5.0-star reputation to every renovation project in the ${cityDisplay} area.`}
        bullets={[
          `Kitchen remodeling in ${cityDisplay} — custom layouts, cabinetry, countertops`,
          `Bathroom remodeling in ${cityDisplay} — spa-level finishes and efficient design`,
          `Full home renovations in ${cityDisplay} — cohesive transformation from start to finish`,
          `Interior upgrades in ${cityDisplay} — flooring, trim, and custom work`,
        ]}
      />

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us */}
      <WhyChooseUs />

      {/* 7. Service Areas */}
      <ServiceAreasSection
        headingText={`Serving ${cityDisplay} and Surrounding Communities`}
        bodyText={`Bay City Remodeling is proud to serve homeowners in ${cityDisplay} and all surrounding areas. Our local expertise means we understand the homes, styles, and expectations of residents throughout the region.`}
      />

      {/* 8. Testimonials */}
      <TestimonialsSection />

      {/* 9. FAQ */}
      <FAQSection faqs={faqs} title={`Remodeling in ${cityDisplay} — Questions Answered`} />

      {/* 10. Final CTA */}
      <FinalCTA
        title={`Start Your Remodeling Project in ${cityDisplay}`}
        subtitle="Your home should reflect how you want to live today. Bay City Remodeling is ready to bring your vision to life."
      />

      <Footer />
    </div>
  );
}
